import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, List, ListItem, ListItemIcon, Avatar, Chip, Divider } from '@mui/material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import { CheckCircleOutline } from '@mui/icons-material';
import { useChatContext } from '../../agent/contexts/ChatContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { flowiseService } from '../../agent/services/FlowiseService';

interface GenericInfoData {
  resumo?: string;
  url?: string;
  [key: string]: any;
}

interface GenericInfoViewProps {
  infoData: GenericInfoData;
  onNavigate?: (url: string) => void;
}

const sanitizeResumo = (raw?: string): string => {
  if (!raw) return '';
  let text = raw.trim();

  // Desembrulhar até 3 camadas de JSON aninhado procurando resumo/mensagem
  for (let i = 0; i < 3; i++) {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed === 'string') {
        text = parsed.trim();
        continue;
      }
      if (parsed && typeof parsed === 'object') {
        if (typeof (parsed as any).resumo === 'string') {
          text = ((parsed as any).resumo as string).trim();
          continue;
        }
        if (typeof (parsed as any).message === 'string') {
          text = ((parsed as any).message as string).trim();
          continue;
        }
      }
    } catch {
      // Não é JSON, encerra
    }
    break;
  }

  // Remover "Resposta:" (com variações em markdown)
  text = text.replace(/^\s*(\*\*\s*)?Resposta(\s*\*\*)?\s*[:\-–—]\s*/i, "");

  // Remover rótulos em negrito/itálico/código no início
  text = text.replace(/^\s*\*\*[^*]+:\s*\*\*\s*/i, ""); // **Label:**
  text = text.replace(/^\s*\*\*[^*]+\*\*\s*:?:?\s*/i, ""); // **Label**:
  text = text.replace(/^\s*\*[^*]+:\s*\*\s*/i, "");      // *Label:*
  text = text.replace(/^\s*_[^_]+:\s*_\s*/i, "");        // _Label:_
  text = text.replace(/^\s*`[^`]+`:\s*/i, "");           // `Label`:

  // Remover ocorrência residual "**: **"
  text = text.replace(/^\s*\*\*\s*[:\-–—]\s*\*\*\s*/i, "");

  // Remover aspas/backticks nas pontas
  text = text.replace(/^[`"'“”]+/, '').replace(/[`"'“”]+$/, '');

  return text.trim();
};

function isMarkdownLike(text: string): boolean {
  if (!text) return false;
  const s = text.trim();
  if (!s) return false;
  return /(\n|^)\s*```/.test(s) || /(\n|^)\s*#{1,6}\s+/.test(s) || /(\n|^)\s*[-*]\s+/.test(s) || /\*\*[^*]+\*\*/.test(s) || /`[^`]+`/.test(s);
}

const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const bold = text.slice(i).match(/^\*\*([^*]+)\*\*/);
    if (bold) {
      nodes.push(<Typography component="span" sx={{ fontWeight: 700 }}>{bold[1]}</Typography>);
      i += bold[0].length;
      continue;
    }
    const italic = text.slice(i).match(/^\*([^*]+)\*/) || text.slice(i).match(/^_([^_]+)_/);
    if (italic) {
      nodes.push(<Typography component="span" sx={{ fontStyle: 'italic' }}>{italic[1]}</Typography>);
      i += italic[0].length;
      continue;
    }
    const strike = text.slice(i).match(/^~~([^~]+)~~/);
    if (strike) {
      nodes.push(<Typography component="span" sx={{ textDecoration: 'line-through' }}>{strike[1]}</Typography>);
      i += strike[0].length;
      continue;
    }
    const code = text.slice(i).match(/^`([^`]+)`/);
    if (code) {
      nodes.push(<Box component="span" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>{code[1]}</Box>);
      i += code[0].length;
      continue;
    }
    const link = text.slice(i).match(/^\[([^\]]+)\]\(([^\)]+)\)/);
    if (link) {
      nodes.push(
        <Box component="a" href={link[2]} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
          {link[1]}
        </Box>
      );
      i += link[0].length;
      continue;
    }
    nodes.push(text[i]);
    i += 1;
  }
  return nodes;
};

const renderMarkdown = (text: string): React.ReactNode => {
  const parts: { type: 'code' | 'text'; content: string; lang?: string }[] = [];
  let pos = 0;
  while (pos < text.length) {
    const start = text.indexOf('```', pos);
    if (start === -1) {
      parts.push({ type: 'text', content: text.slice(pos) });
      break;
    }
    if (start > pos) parts.push({ type: 'text', content: text.slice(pos, start) });
    const end = text.indexOf('```', start + 3);
    const segment = end === -1 ? text.slice(start + 3) : text.slice(start + 3, end);
    const m = segment.match(/^([a-zA-Z0-9_-]+)\s*(?:\n|\s)?/);
    let lang: string | undefined = undefined;
    let code = segment;
    if (m) {
      lang = m[1];
      code = segment.slice(m[0].length);
    }
    parts.push({ type: 'code', content: code, lang });
    pos = end === -1 ? text.length : end + 3;
  }

  const normalizeMarkdownInline = (s: string): string => {
    return s.replace(/(\S)\s+(#{1,6})\s+/g, '$1\n$2 ');
  };

  const renderText = (block: string, keyPrefix: string) => {
    const lines = normalizeMarkdownInline(block).split(/\r?\n/);
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) { i++; continue; }
      const h = line.match(/^\s*(#{1,6})\s+(.*)$/);
      if (h) {
        const level = h[1].length;
        const textLine = h[2];
        const variant = level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1';
        nodes.push(<Typography key={`${keyPrefix}-h-${i}`} variant={variant} sx={{ fontWeight: 700 }}>{renderInline(textLine)}</Typography>);
        i++; continue;
      }
      if (/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
        nodes.push(<Divider key={`${keyPrefix}-hr-${i}`} sx={{ my: 1 }} />);
        i++; continue;
      }
      if (/^\s*>\s?/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*>\s?/, ''));
          i++;
        }
        nodes.push(
          <Box key={`${keyPrefix}-q-${i}`} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5, my: 1 }}>
            {items.map((t, idx) => (
              <Typography key={`${keyPrefix}-q-${i}-${idx}`} variant="body1">{renderInline(t)}</Typography>
            ))}
          </Box>
        );
        continue;
      }
      const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
      const olMatch = line.match(/^\s*(\d{1,3})\.\s+(.*)$/);
      if (ulMatch) {
        const items: string[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*[-*]\s+(.*)$/);
          if (!m2) break;
          items.push(m2[1]);
          i++;
        }
        nodes.push(
          <Box key={`${keyPrefix}-ul-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2, my: 0.5 }}>
            {items.map((t, idx) => (
              <Typography key={`${keyPrefix}-ul-${i}-${idx}`} variant="body1" sx={{ display: 'flex', gap: 1 }}>
                <Box component="span">•</Box>
                <Box component="span" sx={{ flex: 1 }}>{renderInline(t)}</Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }
      if (olMatch) {
        const items: {n: string, t: string}[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*(\d{1,3})\.\s+(.*)$/);
          if (!m2) break;
          items.push({ n: m2[1], t: m2[2] });
          i++;
        }
        nodes.push(
          <Box key={`${keyPrefix}-ol-${i}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2, my: 0.5 }}>
            {items.map((it, idx) => (
              <Typography key={`${keyPrefix}-ol-${i}-${idx}`} variant="body1" sx={{ display: 'flex', gap: 1 }}>
                <Box component="span">{it.n}.</Box>
                <Box component="span" sx={{ flex: 1 }}>{renderInline(it.t)}</Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }
      const para: string[] = [];
      while (i < lines.length && lines[i].trim() && !/^\s*(#{1,6})\s+/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i]) && !/^\s*\d{1,3}\.\s+/.test(lines[i]) && !/^\s*>\s?/.test(lines[i])) {
        para.push(lines[i].trim());
        i++;
      }
      nodes.push(<Typography key={`${keyPrefix}-p-${i}`} variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{renderInline(para.join(' '))}</Typography>);
    }
    return nodes;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {parts.map((p, idx) => {
        if (p.type === 'code') {
          if ((p.lang || '').toLowerCase() === 'md' || (p.lang || '').toLowerCase() === 'markdown' || (p.lang || '').toLowerCase() === 'mdx' || (p.lang || '').toLowerCase() === 'text') {
            return <Box key={`md-${idx}`}>{renderMarkdown(p.content)}</Box>;
          }
          return (
            <Box key={`code-${idx}`} sx={{ bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography component="pre" sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{p.content}</Typography>
            </Box>
          );
        }
        return <Box key={`text-${idx}`}>{renderText(p.content, `t-${idx}`)}</Box>;
      })}
    </Box>
  );
};

const parseContent = (text?: string) => {
  const cleaned = sanitizeResumo(text);
  if (!cleaned) return { intro: '', bullets: [] as string[] };

  const lines = cleaned.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
  const bullets: string[] = [];
  const introParts: string[] = [];
  let foundBullet = false;

  for (const line of lines) {
    if (/^[-*•]\s+/.test(line)) {
      bullets.push(line.replace(/^[-*•]\s+/, ''));
      foundBullet = true;
    } else {
      if (!foundBullet) {
        introParts.push(line);
      }
    }
  }

  const intro = introParts.join(' ');
  return { intro, bullets };
};

const GenericInfoView: React.FC<GenericInfoViewProps> = ({ infoData }) => {
  const { resumo } = infoData || {};
  const { intro, bullets } = parseContent(resumo);
  const { 
    sendMessage, 
    activeSessionId, 
    createSession, 
    setActiveSession, 
    organizations, 
    selectedOrganization,
    isProcessing
  } = useChatContext();
  const { authState } = useAuthContext();
  const [selectedFlow, setSelectedFlow] = useState<string>('');

  const resolveOrganizationId = (): string | undefined => {
    const org = selectedOrganization || (organizations && organizations[0]);
    return (org as any)?._key || org?.id;
  };

  // Carregar fluxo padrão para garantir envio sem erro, seguindo padrão pós-cadastro
  useEffect(() => {
    const loadDefaultFlow = async () => {
      const userId = authState.profileId;
      if (!userId) return;
      try {
        const flows = await flowiseService.getAvailableFlows(userId);
        setSelectedFlow(flows[0]?.id || 'master');
      } catch (err) {
        console.error('Erro ao carregar flows:', err);
      }
    };
    loadDefaultFlow();
  }, [authState.profileId]);

  const quickActions = [
    'Quero cadastrar um animal',
    'Quero registrar uma ordenha de um animal',
    'Quero registrar pesagem para um animal',
    'Quero registrar uma alimentação para um animal',
  ];

  const handleQuickAction = async (label: string) => {
    try {
      // Criar sessão se não houver uma ativa (padrão pós-cadastro)
      if (!activeSessionId) {
        const orgId = resolveOrganizationId();
        if (!orgId) {
          console.error('Nenhuma organização disponível para iniciar a conversa');
          return;
        }
        const newSessionId = await createSession(orgId);
        await setActiveSession(newSessionId);
      }

      // Garantir selectedFlow; se não tiver, tenta carregar on-demand
      let flowId = selectedFlow;
      if (!flowId) {
        const userId = authState.profileId;
        if (!userId) {
          console.error('Sem userId para carregar flows');
        } else {
          try {
            const flows = await flowiseService.getAvailableFlows(userId);
            flowId = flows[0]?.id || 'master';
            setSelectedFlow(flowId);
          } catch (err) {
            console.error('Erro ao carregar flows on-demand:', err);
          }
        }
      }

      await sendMessage(label, flowId);
    } catch (error) {
      console.error('Erro ao enviar ação rápida:', error);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card
        sx={{
          p: 3,
          border: `1px solid ${DESIGN_TOKENS.colors.organization.farm}22`,
          background: `linear-gradient(180deg, ${DESIGN_TOKENS.colors.organization.farm}0A 0%, transparent 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, minHeight: '50vh', justifyContent: 'center' }}>
          {/* Avatar maior e centralizado com logo da Muu */}
          <Avatar
            src="/logo.svg"
            alt="Muu"
            sx={{
              width: 80,
              height: 80,
              bgcolor: DESIGN_TOKENS.colors.background.paper,
              border: `3px solid ${DESIGN_TOKENS.colors.primary[200]}`,
              boxShadow: 2,
              '& .MuiAvatar-img': {
                objectFit: 'contain',
                padding: '8px',
              },
            }}
          />

          {/* Conteúdo retornado pelo Flowise (intro e bullets) */}
          {(() => {
            const text = sanitizeResumo(resumo);
            if (!text) return null;
            if (isMarkdownLike(text)) {
              return <Box sx={{ width: '100%', maxWidth: 760, textAlign: 'left' }}>{renderMarkdown(text)}</Box>;
            }
            return (
              <Typography variant="body1" sx={{ maxWidth: 760, textAlign: 'left' }}>
                {parseContent(text).intro}
              </Typography>
            );
          })()}
          {!isMarkdownLike(sanitizeResumo(resumo)) && bullets.length > 0 && (
            <List dense sx={{ width: '100%', maxWidth: 760 }}>
              {bullets.map((b, idx) => (
                <ListItem key={idx} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckCircleOutline sx={{ color: DESIGN_TOKENS.colors.primary[400], fontSize: 18 }} />
                  </ListItemIcon>
                  <Typography variant="body2">{b}</Typography>
                </ListItem>
              ))}
            </List>
          )}

          {/* Seção de ações rápidas */}
          <Box sx={{ mt: 3, width: '100%', maxWidth: 760 }}>
            <Typography
              variant="subtitle2"
              sx={{
                textAlign: 'center',
                color: DESIGN_TOKENS.colors.text.secondary,
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Você também pode...
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {quickActions.map((label) => (
                <Chip
                  key={label}
                  label={label}
                  clickable
                  onClick={() => handleQuickAction(label)}
                  variant="outlined"
                  color="primary"
                  sx={{ fontSize: 12 }}
                  disabled={isProcessing}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default GenericInfoView;